import { sanitize } from "cy-logging";
import { DatadogLogEntry } from "./types";

/**
 * Formats log entries for Vercel-Datadog integration compatibility.
 *
 * Vercel's Datadog integration expects logs in specific formats to properly
 * parse and forward them. This formatter ensures compatibility.
 */
export class DatadogFormatter {
  private source: "server" | "client" | "universal";

  constructor(source: "server" | "client" | "universal" = "universal") {
    this.source = source;
  }

  /**
   * Formats a log entry with consistent structure for both client and server
   *
   * @param level - Log level
   * @param message - Log message
   * @param context - Additional context data
   * @returns Formatted log entry
   */
  format(
    level: string,
    message: string,
    context: Record<string, any> & {
      service?: string;
      source: string;
      environment?: string;
      correlationId: string;
      component: string;
    },
  ): { message: string; context: Record<string, any> } {
    const optional = {
      service: context.service,
      environment: context.environment,
    };
    return {
      message,
      context: {
        timestamp: new Date().toISOString(),
        level: level.toUpperCase(),
        source: context.source || this.source,
        ...optional,
        correlationId: context.correlationId,
        component: context.component,
        ...sanitize(context),
      },
    };
  }

  /**
   * Formats a log entry for Vercel's log drain to Datadog.
   *
   * Vercel forwards logs that are:
   * 1. JSON formatted
   * 2. Include standard fields (level, message, timestamp)
   * 3. Have proper service/environment tags
   *
   * @param entry - The log entry to format
   * @returns Formatted JSON string
   */
  static formatForVercel(entry: DatadogLogEntry): string {
    try {
      // Vercel-specific format with @timestamp field
      const vercelEntry = {
        "@timestamp": entry.timestamp,
        level: entry.level.toUpperCase(),
        message: entry.message,
        service: entry.service,
        ddsource: "nodejs", // or "browser" for client logs
        ddtags: `env:${entry.environment}`,
        hostname: process.env.VERCEL_URL || "localhost",
        logger: {
          name: entry.service,
        },
        // Include correlation data
        usr: {
          correlationId: entry.correlationId,
          ...(entry.sessionId && { sessionId: entry.sessionId }),
        },
      };

      // Don't spread the original entry to avoid duplicating fields
      Object.keys(entry).forEach((key) => {
        if (!Object.hasOwn(vercelEntry, key) && key !== "level") {
          (vercelEntry as any)[key] = sanitize((entry as any)[key]);
        }
      });

      return JSON.stringify(vercelEntry);
    } catch (error) {
      // Fallback to basic log entry if JSON serialization fails
      const basicEntry = {
        "@timestamp": entry.timestamp,
        level: entry.level.toUpperCase(),
        message: entry.message,
        service: entry.service,
        error: "JSON_SERIALIZATION_FAILED",
        original_error: error instanceof Error ? error.message : String(error),
      };

      try {
        return JSON.stringify(basicEntry);
      } catch (fallbackError) {
        // Ultimate fallback - return a simple string
        return `{"level":"${entry.level.toUpperCase()}","message":"${entry.message}","error":"SERIALIZATION_FAILED"}`;
      }
    }
  }

  /**
   * Formats error objects for proper Datadog error tracking.
   *
   * @param error - The error to format
   * @param context - Additional context
   * @returns Formatted error object
   */
  static formatError(error: Error): Record<string, any> {
    return {
      kind: error.name || "Error",
      message: error.message,
      stack: error.stack,
      // Extract file and line number if available
      ...(error.stack && {
        file: this.extractFileFromStack(error.stack),
        line: this.extractLineFromStack(error.stack),
      }),
    };
  }

  /**
   * Sanitizes sensitive data from log entries.
   * @deprecated Use sanitize from cy-logging instead
   */
  static sanitize = sanitize;

  /**
   * Extracts file path from error stack trace.
   * @private
   */
  private static extractFileFromStack(stack: string): string | undefined {
    const match = stack.match(/at\s+.*?\s+\((.*?):\d+:\d+\)/);
    return match ? match[1] : undefined;
  }

  /**
   * Extracts line number from error stack trace.
   * @private
   */
  private static extractLineFromStack(stack: string): number | undefined {
    const match = stack.match(/at\s+.*?\s+\(.*?:(\d+):\d+\)/);
    return match ? parseInt(match[1], 10) : undefined;
  }

  /**
   * Truncates large objects to prevent log size issues.
   *
   * @param obj - Object to truncate
   * @param maxDepth - Maximum depth to traverse
   * @param maxArrayLength - Maximum array length
   * @returns Truncated object
   */
  static truncate(
    obj: any,
    maxDepth: number = 5,
    maxArrayLength: number = 100,
  ): any {
    const truncateRecursive = (value: any, depth: number): any => {
      if (depth > maxDepth) {
        return "[TRUNCATED: MAX_DEPTH]";
      }

      if (value === null || value === undefined) {
        return value;
      }

      if (Array.isArray(value)) {
        if (value.length > maxArrayLength) {
          return [
            ...value
              .slice(0, maxArrayLength)
              .map((v) => truncateRecursive(v, depth + 1)),
            `[TRUNCATED: ${value.length - maxArrayLength} more items]`,
          ];
        }
        return value.map((v) => truncateRecursive(v, depth + 1));
      }

      if (typeof value === "object") {
        const result: Record<string, any> = {};
        const keys = Object.keys(value);

        keys.forEach((key) => {
          result[key] = truncateRecursive(value[key], depth + 1);
        });

        return result;
      }

      // For strings, truncate if too long
      if (typeof value === "string" && value.length > 1000) {
        return value.substring(0, 1000) + "[TRUNCATED]";
      }

      return value;
    };

    return truncateRecursive(obj, 0);
  }
}
